import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { AmbientOrb } from "../components/AmbientOrb";
import { Marquee } from "../components/Marquee";

const heroLines = ["Return to Your Center.", "Quiet the Noise Within."];

const HeroLine = ({ text, index }) => (
  <span className="block overflow-hidden">
    <motion.span
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 0.9, delay: 0.2 + index * 0.15, ease: [0.22, 1, 0.36, 1] }}
      className="block"
    >
      {text}
    </motion.span>
  </span>
);

const CHAPTERS = [
  { n: "01", title: "Unmasking Burnout", text: "You've been holding it together for so long. Here, you can finally set it down." },
  { n: "02", title: "Reclaiming Breath", text: "Nervous system safety and gentle presence, before anything else." },
  { n: "03", title: "Sustainable Soul Alignment", text: "Slow, steady reconnection with your own inner wisdom — at your pace." },
];

export default function WelcomePage() {
  const navigate = useNavigate();
  return (
    <motion.main
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35 }}
      data-testid="welcome-page"
    >
      <section
        className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden px-6 py-28"
        style={{ background: "radial-gradient(circle at 50% 30%, rgba(226,234,228,0.6) 0%, rgba(251,249,245,1) 70%)" }}
      >
        <AmbientOrb />
        <div className="relative z-10 max-w-3xl text-center">
          <p className="text-xs sm:text-sm uppercase tracking-[0.3em] font-semibold text-[#4A6B56]/80 mb-6">
            Aurea Soul &middot; Soul-Centred Coaching
          </p>
          <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.15] text-[#1E2D24]">
            {heroLines.map((l, i) => (
              <HeroLine key={i} text={l} index={i} />
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="mt-6 text-base sm:text-lg text-[#5F6A62] leading-relaxed max-w-xl mx-auto"
          >
            A gentle, guided space for anxious, burned-out early-career and mid-career professionals ready
            to feel calm, clear, and reconnected to themselves — one soft question at a time.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.6 }}
          >
            <button
              data-testid="begin-journey-button"
              onClick={() => navigate("/discover")}
              className="mt-10 inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#4A6B56] text-[#FBF9F5] font-medium hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300"
            >
              Begin Your Clarity Journey <ArrowRight className="w-4 h-4" />
            </button>
            <p className="mt-4 text-xs text-[#5F6A62]/80">Takes 3 minutes &middot; No obligation &middot; Completely free</p>
          </motion.div>
        </div>
      </section>

      <Marquee />

      <section className="relative px-6 py-24 bg-[#FBF9F5]">
        <div className="max-w-4xl mx-auto space-y-16">
          {CHAPTERS.map((c, i) => (
            <motion.article
              key={c.n}
              data-testid={`manifesto-chapter-${i}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="flex flex-col sm:flex-row gap-6 sm:gap-10 border-b border-[#4A6B56]/10 pb-12 last:border-0"
            >
              <span className="font-serif text-5xl sm:text-6xl text-[#C47B5E]/70 shrink-0">{c.n}</span>
              <div>
                <h2 className="font-serif text-2xl sm:text-3xl text-[#1E2D24] mb-3">{c.title}</h2>
                <p className="text-[#5F6A62] text-base sm:text-lg leading-relaxed max-w-xl">{c.text}</p>
              </div>
            </motion.article>
          ))}
        </div>
      </section>
    </motion.main>
  );
}
